import { useCallback, useEffect, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import { LogOut, Users, ChefHat, BarChart3, LayoutGrid, Bell, Utensils } from "lucide-react";
import { staffApi } from "./staffApi";
import { unlockAudio, playTone, audioUnlocked } from "./alerts";
import { AlertPrefs } from "./AlertPrefs";
import { Board } from "./Board";
import { Kitchen } from "./Kitchen";
import { OrderDetail } from "./OrderDetail";
import StaffHome from "./StaffHome";
import { StaffDishes } from "./StaffDishes";
import ManagerArea from "./ManagerArea";
import { ShiftSummary } from "./ShiftSummary";
import { PinModal } from "./PinModal";
import "./staff.css";

export default function StaffApp({ initialView = "home", embedded = false }) {
  const [staff, setStaff] = useState(() => {
    try { return sessionStorage.getItem("ut_staff_token") ? JSON.parse(sessionStorage.getItem("ut_staff")) : null; }
    catch { return null; }
  });
  const [dev, setDev] = useState(null);
  const [view, setView] = useState(initialView);
  const [openOrder, setOpenOrder] = useState(null);
  const [manager, setManager] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const [audioOk, setAudioOk] = useState(() => audioUnlocked());
  const [trustErr, setTrustErr] = useState("");
  const lockRef = useRef(null);
  const lockMinRef = useRef(5);

  useEffect(() => {
    staffApi.get("/staff/device/state")
      .then((r) => { lockMinRef.current = r.data.lock_minutes || 5; setDev(r.data); })
      .catch(() => setDev({ trusted: false, device_id: null, lock_minutes: 5 }));
  }, []);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem("ut_staff_token");
    sessionStorage.removeItem("ut_staff");
    setStaff(null);
    setManager(null);
  }, []);

  useEffect(() => {
    const m = document.querySelector('meta[name="robots"]');
    const prev = m ? m.content : null;
    if (m) m.content = "noindex,nofollow";
    document.title = "Urban Turban — Staff";
    window.addEventListener("ut-staff-logout", clearSession);
    return () => { if (m && prev !== null) m.content = prev; window.removeEventListener("ut-staff-logout", clearSession); };
  }, [clearSession]);

  // Inactivity demotes this DEVICE to viewer mode — a screen lock, never a shift end,
  // never an order release. Shift and orders stay exactly where they were.
  const resetLock = useCallback(() => {
    clearTimeout(lockRef.current);
    lockRef.current = setTimeout(() => {
      sessionStorage.removeItem("ut_staff_token");
      sessionStorage.removeItem("ut_staff");
      setStaff(null);
      setManager(null);
    }, (lockMinRef.current || 5) * 60 * 1000);
  }, []);

  useEffect(() => {
    if (!staff) return undefined;
    const evs = ["pointerdown", "touchstart", "keydown"];
    evs.forEach((e) => window.addEventListener(e, resetLock, { passive: true }));
    resetLock();
    return () => { clearTimeout(lockRef.current); evs.forEach((e) => window.removeEventListener(e, resetLock)); };
  }, [staff, resetLock]);

  const handleLogin = (r) => {
    sessionStorage.setItem("ut_staff_token", r.token);
    sessionStorage.setItem("ut_staff", JSON.stringify({ staff_id: r.staff_id, name: r.name, role: r.role }));
    if (r.device_token) localStorage.setItem("ut_device_token", r.device_token);
    setStaff({ staff_id: r.staff_id, name: r.name, role: r.role });
    setAudioOk(audioUnlocked());
    setDev((d) => ({ ...(d || { lock_minutes: 5 }), trusted: true, device_id: d?.device_id || r.device_id }));
  };

  if (!dev) return <div className="staff-root min-h-screen" />;

  if (!dev.trusted && !staff) {
    return (
      <div className="staff-root min-h-screen text-white">
        <Toaster position="top-center" theme="dark" richColors />
        <div data-testid="staff-login" className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
          <img src="/urban-emblem.png" alt="Urban Turban" className="h-16 drop-shadow-[0_0_18px_rgba(212,175,55,0.35)]" />
          <p className="text-[11px] tracking-[0.5em] text-[#D4AF37] font-bold mt-6">URBAN TURBAN</p>
          <h1 className="text-2xl font-bold tracking-tight mt-1">New device</h1>
          <p className="text-white/45 text-sm mt-2 max-w-xs">Enter any staff PIN once to trust this device for the floor screens.</p>
        </div>
        <PinModal title="Trust this device" subtitle="Any staff PIN — the owner can revoke it later" error={trustErr}
          onClose={() => {}}
          onSubmit={async (pin) => {
            try {
              const r = await staffApi.post("/staff/login", { pin });
              await unlockAudio();
              handleLogin(r.data);
              setView("home");
            } catch (e) {
              setTrustErr(e.response?.status === 429 ? "Too many tries — wait a few minutes" : "Wrong PIN");
              setTimeout(() => setTrustErr(""), 2500);
            }
          }} />
      </div>
    );
  }

  return (
    <div data-testid="staff-app" className="staff-root min-h-screen text-white">
      <Toaster position="top-center" theme="dark" richColors />
      <header className="sticky z-40 border-b border-white/8" style={{ top: embedded ? 44 : 0, background: "rgba(16,16,19,0.92)", backdropFilter: "blur(14px)" }}>
        <div className="flex items-center justify-between px-4 h-16">
          <button data-testid="staff-home-logo" onClick={() => { setView("home"); setOpenOrder(null); }} className="flex items-center gap-2.5">
            <img src="/urban-emblem.png" alt="" className="h-8" />
            <span className="font-black tracking-widest text-sm hidden sm:inline">URBAN TURBAN <span className="text-[#D4AF37]">VEERGI</span></span>
          </button>
          <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
            <span data-testid="staff-identity" className="shrink-0 text-xs font-bold text-white/70 bg-white/5 border border-white/10 rounded-full px-3 py-2">
              {staff ? (<>{staff.name} · <span className="text-[#D4AF37] uppercase">{staff.role === "waiter" ? "VeerGi" : staff.role}</span></>)
                : (<span className="text-white/50">FLOOR DEVICE</span>)}
            </span>
            {[["home", Users, "STAFF", "staff-home-btn"],
              ["board", LayoutGrid, "BOARD", "staff-board-btn"],
              ["kitchen", ChefHat, "KITCHEN", "staff-kitchen-btn"],
              ["dishes", Utensils, "DISHES", "staff-dishes-btn"],
              ["shift", BarChart3, "SHIFT", "staff-shift-btn"]].map(([v, Icon, label, tid]) => (
              <button key={v} data-testid={tid} onClick={() => { setView(v); setOpenOrder(null); }}
                className={`shrink-0 h-10 px-4 rounded-full border text-xs font-black tracking-widest flex items-center gap-1.5 ${view === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70 hover:border-[#D4AF37]/50"}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
            {staff && (
              <button data-testid="staff-alert-prefs-btn" onClick={() => setShowPrefs(true)} aria-label="Alert settings"
                className="shrink-0 w-10 h-10 rounded-full border border-white/15 text-white/70 hover:border-[#D4AF37]/50 flex items-center justify-center">
                <Bell size={15} />
              </button>
            )}
            {staff && (
              <button data-testid="staff-leave" aria-label="Leave board"
                onClick={() => { clearSession(); toast("Left the Board — your shift is still on. End it from your tile when you're done."); }}
                className="h-10 w-10 rounded-full border border-white/15 text-white/60 flex items-center justify-center hover:border-[#F0B429]/60 hover:text-[#F0B429]"><LogOut size={15} /></button>
            )}
          </div>
        </div>
        <div className="gold-line" />
      </header>
      {staff && !audioOk && (
        <button data-testid="start-shift-audio" onClick={async () => {
          const ok = await unlockAudio();
          setAudioOk(ok);
          if (ok) { playTone(3); toast.success("Sound is on — you'll hear every alert"); }
          else toast.error("Sound is blocked by this browser — alerts will be silent. Check the tab's sound permissions.");
        }} className="w-full bg-[#D4AF37] text-black font-black text-sm tracking-widest py-3">
          🔔 TAP TO TURN ON SOUND FOR THIS DEVICE
        </button>
      )}
      {showPrefs && staff && <AlertPrefs onClose={() => setShowPrefs(false)} />}
      {manager && (
        <ManagerArea mgr={manager} onExit={() => setManager(null)}
          onOpenOrder={(oid) => { setView("board"); setOpenOrder(oid); }} />
      )}
      {view === "home" ? (
        <StaffHome person={staff} onLogin={handleLogin} onGoBoard={() => setView("board")}
          onManager={(r) => { handleLogin(r); setManager({ staff_id: r.staff_id, name: r.name, role: r.role }); }} />
      ) : view === "kitchen" ? (
        <Kitchen />
      ) : view === "dishes" ? (
        <StaffDishes />
      ) : view === "shift" ? (
        <ShiftSummary staff={staff} />
      ) : openOrder ? (
        <OrderDetail orderId={openOrder} staff={staff} onBack={() => setOpenOrder(null)} />
      ) : (
        <Board person={staff} onOpen={(oid) => setOpenOrder(oid)} onGoHome={() => setView("home")} />
      )}
    </div>
  );
}
